import { Grid } from "@material-ui/core";
import { Box, Paper, Typography } from "@mui/material";
import { useState, useRef } from "react";
import { useBlockedDates } from "../../../hooks/useBlockedDates";
import { SaveAndCancelButton } from "../../../components/buttons";
import { AppDatePicker } from "../../../components/datepickers";
import { AppModalDialogBox } from "../../../components/modals";
import { GLOBAL_TEXT } from "../../../constants";
import {
  convertToTimezone,
  DATE_FORMATS,
} from "../../../services/date-and-time.utils";
import { checkTimeslotSelectionForLateNight } from "../../../services/validators";
import { EmailAddressBox } from "../../components/email-addressbox";
import { SlotDetailsCard } from "../../../components/cards/slot-details-card";
import { AccessTime, CalendarToday } from "@mui/icons-material";

function RescheduleInvitationModal({
  handleModalClose,
  invitationId,
  handleSubmit,
  invitationData,
  selectedValue,
  additionalMembers = [],
}) {
  const initialSelectedDateTime = convertToAmbassadorAndClientTimezone(
    new Date(selectedValue?.from)
  );
  const [selectedDateTime, setSelectedDateTime]: any = useState(
    initialSelectedDateTime
  );
  // Extract email addresses from additionalMembers array of objects
  // Store in ref to maintain stable reference and prevent infinite loops
  const initialEmailListRef = useRef<string[]>(
    additionalMembers?.map((member) => member?.email).filter(Boolean) || []
  );
  const [emailIds, setEmailds]: any = useState({
    list: [],
    invalidEmailIds: [],
  });
  const [notify, setNotify] = useState("");
  const { blockedDates, onMonthChange } = useBlockedDates();

  function convertToAmbassadorAndClientTimezone(date) {
    const to = new Date(date?.getTime() + 45 * 60000);
    return {
      from: date,
      to,
      clientTimezoneConvertedDateTimeFrom: convertToTimezone(
        date,
        invitationData?.client?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      clientTimezoneConvertedDateTimeTo: convertToTimezone(
        to,
        invitationData?.client?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      ambassadorTimezoneConvertedDateTimeFrom: convertToTimezone(
        date,
        invitationData?.ambassador?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      ambassadorTimezoneConvertedDateTimeTo: convertToTimezone(
        to,
        invitationData?.ambassador?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
    };
  }

  const handleDateChange = (date) => {
    const selectedDatetimes = convertToAmbassadorAndClientTimezone(date);

    const isValid = [
      "clientTimezoneConvertedDateTimeFrom",
      "ambassadorTimezoneConvertedDateTimeFrom",
    ]
      .map((datetime) => {
        return checkTimeslotSelectionForLateNight(selectedDatetimes[datetime]);
      })
      .every((datetime) => datetime === false);

    !isValid
      ? setNotify(GLOBAL_TEXT.INVITATION_RESCHEDULE_LATE_NIGHT_MESSAGE)
      : setNotify("");

    setSelectedDateTime(selectedDatetimes);
  };

  const handleOnEmailAddressesChange = (payload) => {
    setEmailds(payload);
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    if (!emailIds?.invalidEmailIds?.length) {
      const payload = {
        updatedTimeSlot: {
          to: selectedDateTime.to,
          from: selectedDateTime.from,
        },
        additionalMembers: emailIds?.list?.map((email) => {
          return { email };
        }),
        id: invitationId,
      };

      handleSubmit(payload);
    }
  };

  return (
    <AppModalDialogBox
      isModalOpen={true}
      title="Reschedule"
      handleClose={handleModalClose}
      actionButtonRenderer={
        <SaveAndCancelButton
          primaryText={"Reschedule"}
          defaultText={"Cancel"}
          onPrimaryActionClick={onSubmit}
          onDefaultButtonClick={handleModalClose}
          isPrimaryButtonDisabled={
            emailIds?.invalidEmailIds?.length ? true : false
          }
        />
      }
      size={"sm"}
    >
      <Grid container spacing={2} direction="column">
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: "1px solid #e0e0e0",
              backgroundColor: "#fafafa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <CalendarToday sx={{ mr: 1.5, color: "primary.main" }} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  color: "text.primary",
                  fontSize: 16,
                }}
              >
                Select Date & Time
              </Typography>
            </Box>

            <AppDatePicker
              dateFormat={"Pp"}
              showTimeSelect
              selectedDate={selectedDateTime?.from}
              setDate={(date) => handleDateChange(date)}
              minDate={new Date()}
              blockedDates={blockedDates}
              onMonthChange={onMonthChange}
            />
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: "text.primary",
              display: "flex",
              alignItems: "center",
              fontSize: 16,
            }}
          >
            <AccessTime sx={{ mr: 1.5, color: "primary.main" }} />
            Meeting Times
          </Typography>
        </Grid>

        <Grid item container spacing={1}>
          <Grid item xs={12} sm={6}>
            <SlotDetailsCard
              title={`${invitationData?.ambassador?.firstName} (Ambassador)`}
              from={selectedDateTime?.ambassadorTimezoneConvertedDateTimeFrom}
              to={selectedDateTime?.ambassadorTimezoneConvertedDateTimeTo}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SlotDetailsCard
              title={`${invitationData?.client?.firstName} (Vendor)`}
              from={selectedDateTime?.clientTimezoneConvertedDateTimeFrom}
              to={selectedDateTime?.clientTimezoneConvertedDateTimeTo}
            />
          </Grid>
          <Typography
            fontSize={12}
            sx={{ color: "red", mt: 1 }}
          >{`${notify}`}</Typography>
        </Grid>
        <EmailAddressBox 
          onValueChange={handleOnEmailAddressesChange}
          defaultValue={initialEmailListRef.current}
        />
      </Grid>
    </AppModalDialogBox>
  );
}

export { RescheduleInvitationModal };
